import type { SelectChoice } from './types';

const PROVIDER = 'model_config.provider';

export interface LlmModel {
  id: string;
  name: string;
  description?: string | null;
}

/** One provider's model list, named and marked as the app shows that provider. */
export interface LlmCatalog {
  provider: string;
  label: string;
  /** A design-system icon name. */
  logo?: string;
  models: readonly LlmModel[];
}

// The design's house model. The backend has no notion of a recommended model,
// so the tag shows only while a catalog lists this exact id.
const RECOMMENDED_MODEL = 'claude-haiku-4-5-20251001';

/** A model choice's key. Model ids may hold `/`, so the pair is encoded, never joined. */
export const llmModelKey = (provider: string, model: string) =>
  JSON.stringify([provider, model]);

/**
 * The Model card's choices, for `GroupSpecOptions.models`: every catalog in one select. Labels
 * are model ids, as the design shows them, and each choice sets its provider leaf.
 */
export function llmModelChoices(
  catalogs: readonly LlmCatalog[]
): SelectChoice[] {
  return catalogs.flatMap(({ provider, label, logo, models }) =>
    models.map((model) => ({
      // The gateway can list a vendor's id too; the provider keeps the two choices apart.
      key: llmModelKey(provider, model.id),
      value: model.id,
      label: model.id,
      description: [
        label,
        model.description || (model.name !== model.id ? model.name : ''),
      ]
        .filter(Boolean)
        .join('. '),
      recommended: model.id === RECOMMENDED_MODEL,
      logo,
      sets: { [PROVIDER]: provider },
    }))
  );
}

/** A catalog as fetched: `error` is why it could not be listed. */
export type LlmCatalogResult = LlmCatalog & { error?: string };

export interface LlmPicker {
  /** Undefined when no provider listed a model: the card keeps a plain Model field. */
  models?: SelectChoice[];
  /** Offered only without `models`, which would otherwise set the provider. */
  providers?: SelectChoice[];
  /** Set when no provider listed a model; `message` is null for an empty list. */
  failures: Array<{ label: string; message: string | null }>;
}

/**
 * The Model card's picker. A provider that fails is left out while another lists models; when
 * none does, the card gets a Provider select and why each failed, never an empty picker.
 */
export function llmPicker(catalogs: readonly LlmCatalogResult[]): LlmPicker {
  const models = llmModelChoices(catalogs);
  if (models.length > 0) return { models, failures: [] };
  return {
    providers: catalogs.map(({ provider, label, logo }) => ({
      value: provider,
      label,
      logo,
    })),
    failures: catalogs.map(({ label, error }) => ({
      label,
      message: error || null,
    })),
  };
}
